import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteProgram,
  fetchPrograms,
  publishProgram,
} from "../../store/ProgramSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  Card,
  Typography,
  Button,
  CardBody,
  CardFooter,
  Avatar,
  IconButton,
} from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteConfirmation from "../ConfirmBox";
import { AllPrograms } from "../../interfaces/Program";

const ListView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [sortConfig, setSortConfig] = useState<{
    column: string | null;
    sortOrder: "asc" | "desc";
  }>({ column: null, sortOrder: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const itemsPerPage = 8;

  const TABLE_HEAD = [
    { key: "", label: "" },
    { key: "TITLE", label: t("adminPrograms.table.title", "TITLE") },
    { key: "TOTAL COUNSELLINGS", label: t("adminPrograms.table.totalCounsellings", "TOTAL COUNSELLINGS") },
    { key: "BOOKED SLOTS", label: t("adminPrograms.table.bookedSlots", "BOOKED SLOTS") },
    { key: "START DATE", label: t("adminPrograms.table.startDate", "START DATE") },
    { key: "COST", label: t("adminPrograms.table.cost", "COST") },
    { key: "ACTIONS", label: t("adminPrograms.table.actions", "ACTIONS") },
  ];

  useEffect(() => {
    dispatch(fetchPrograms({ search: "",limit: 8 }));
  }, [dispatch]);

  const { data, loading, error } = useSelector(
    (state: RootState) => state.programs
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1 w-full min-h-[300px] py-12">
        <div
          className="border-t-4 border-solid h-12 w-12 border-b-4 border-r-4 border-l-4 rounded-full animate-spin"
          style={{ borderColor: "#B4804A" }}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center flex-1 flex items-center justify-center py-12">
        <Typography
          variant="h6"
          color="blue-gray"
          className="font-medium"
        >
          {t("adminPrograms.noData", "No data available")}
        </Typography>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  const handleDelete = (programId: string) => {
    // Dispatch the delete action
    dispatch(deleteProgram(programId)).then(() => {
      dispatch(fetchPrograms({ search: "" })).then(() => {
        toast.success(t("adminPrograms.deleteSuccess", "Successfully Deleted"));
      });
    });
  };
  const handlePublish = (programId: string, publish: boolean) => {
    dispatch(publishProgram({ programId, publish })).then(() => {
      dispatch(fetchPrograms({ search: "" })).then(() => {
        // toast.success(
        //   publish ? "Successfully Published" : "Successfully Unpublished"
        // );
      });
    });
  };

  const mapColumnToCamelCase = (column: string) => {
    const columnMappings: Record<string, string> = {
      TITLE: "title",      
      "TOTAL COUNSELLINGS": "totalCounsellings",
      "BOOKED SLOTS": "maxParticipants",
      "START DATE": "startDate",
      COST: "cost",
    };

    return columnMappings[column];
  };

  const parsedData = typeof data === "string" ? JSON.parse(data) : data;
  if (!parsedData?.programs || parsedData.programs.length === 0) {
    return (
      <div className="text-center flex-1 flex items-center justify-center py-12">
        <Typography variant="h6" color="blue-gray" className="font-medium">
          {t("adminPrograms.noPrograms", "No programs available")}
        </Typography>
      </div>
    );
  }

  const handleSort = (column: string) => {
    if (column === "" || column === "ACTIONS" || column === "COST") {
      return;
    }
    const updatedColumnName = mapColumnToCamelCase(column);
    const sortOrder =
      updatedColumnName === sortConfig.column && sortConfig.sortOrder === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ column: updatedColumnName, sortOrder });
    dispatch(fetchPrograms({ sortBy: `${updatedColumnName}:${sortOrder}` }));
  };
  const handlePagination = (page: number, limit: number) => {
    dispatch(fetchPrograms({ page, limit }));
  };

  const handleNextClick = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    handlePagination(nextPage, itemsPerPage);
  };

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      const previousPage = currentPage - 1;
      setCurrentPage(previousPage);
      handlePagination(previousPage, itemsPerPage);
    }
  };

  const handleCancel = () => {
    setDeleteItemId(null);
  };

  return (
    <div className="w-full flex-1 flex flex-col justify-between overflow-hidden">
      <div className="w-full overflow-x-auto overflow-y-auto flex-1 px-6">
        <table className="w-full min-w-[750px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head) => (
                <th
                  key={head.key}
                  className="min-w-[80px] py-4 px-4 font-bold text-[#7E8299] uppercase text-xs cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort(head.key)}
                >
                  <div className="flex items-center">
                    <Typography
                      variant="small"
                      className="font-semibold leading-none text-[#7E8299] opacity-70"
                    >
                      {head.label}
                    </Typography>
                    <span className="ml-1"></span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {parsedData?.programs?.map((AllPrograms: AllPrograms) => (
              <tr key={AllPrograms.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Avatar
                      src={AllPrograms.icon}
                      alt={AllPrograms.title}                              
                      className="border border-blue-gray-50 w-10 h-10 bg-blue-gray-50/50 object-contain rounded-md"
                    />
                  </div>
                </td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <Typography
                    variant="small"
                    color="black"
                    className="font-medium"
                  >
                    {AllPrograms.title}
                  </Typography>
                </td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <Typography
                    variant="small"
                    color="black"
                    className="font-medium"
                  >
                    {AllPrograms.hasCounselling !== false ? AllPrograms.totalCounsellings : "-"}
                  </Typography>
                </td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <Typography
                    variant="small"
                    color="black"
                    className="font-medium"
                  >
                    {AllPrograms.maxParticipants}
                  </Typography>
                </td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <Typography
                    variant="small"
                    color="black"
                    className="font-medium"
                  >
                    {moment(AllPrograms.startDate).format(
                      "DD MMM, YYYY"
                    )}
                  </Typography>
                </td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <Typography
                    variant="small"
                    color="black"
                    className="font-medium"
                  >
                    {"$ " + AllPrograms.cost}
                  </Typography>
                </td>

                <td className="py-2.5 px-4 whitespace-nowrap">
                  <div className="relative group text-center inline-block">
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                      id="options-menu"
                    >
                      <img
                        src="./images/dots.png"
                        className="m-auto w-4 h-4 object-contain"
                        alt="action"
                      />
                    </button>

                    <div className="group-hover:block dropdown-menu hidden origin-top-right text-left absolute right-0 mt-1 w-28 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div
                        className="py-1"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <button
                          onClick={() =>
                            handlePublish(
                              AllPrograms.id,
                              !AllPrograms?.publish
                            )
                          }
                          className="block w-full text-left px-4 py-2 text-xs text-[#102832] hover:bg-gray-100"
                          role="menuitem"
                        >
                          {AllPrograms?.publish
                            ? t("adminPrograms.actions.unpublish", "Unpublish")
                            : t("adminPrograms.actions.publish", "Publish")}
                        </button>
                        <Link
                          to={`edit/${AllPrograms.id}`}
                          className="block px-4 py-2 text-xs text-[#102832] hover:bg-gray-100"
                          role="menuitem"
                        >
                          {t("adminPrograms.actions.edit", "Edit")}
                        </Link>
                        <button
                          onClick={() =>
                            setDeleteItemId(AllPrograms.id)
                          }
                          className="block w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-gray-100"
                          role="menuitem"
                        >
                          {t("adminPrograms.actions.remove", "Remove")}
                        </button>
                        {deleteItemId === AllPrograms.id && (
                          <DeleteConfirmation
                            onConfirm={() =>
                              handleDelete(AllPrograms.id)
                            }
                            onCancel={handleCancel}
                            message={AllPrograms.title}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between sm:justify-end border-t border-gray-100 px-4 sm:px-6 py-3 shrink-0 bg-white gap-2 sm:gap-3">
        <Button
          variant="outlined"
          size="sm"
          onClick={handlePreviousClick}
          disabled={currentPage === 1}
          className={`px-3 sm:px-4 py-2 text-xs font-semibold capitalize tracking-normal ${
            currentPage === 1
              ? "text-gray-300 border-gray-200 cursor-not-allowed"
              : "text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {t("adminPrograms.pagination.previous", "Previous")}
        </Button>
        <div className="flex items-center justify-center min-w-[32px] h-8 px-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-md bg-gray-50 select-none">
          {currentPage}
        </div>
        <Button
          variant="outlined"
          size="sm"
          onClick={handleNextClick}
          disabled={(currentPage + 1) > parsedData?.totalPages}
          className={`px-3 sm:px-4 py-2 text-xs font-semibold capitalize tracking-normal ${
            (currentPage + 1) > parsedData?.totalPages
              ? "text-gray-300 border-gray-200 cursor-not-allowed"
              : "text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {t("adminPrograms.pagination.next", "Next")}
        </Button>
      </div>
    </div>
  );
};

export default ListView;
